import { useState } from 'react'
import { Search, LogOut, User } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { getInitials } from '../../utils/avatar'
import type { UserRole } from '../../types'

interface TopBarProps {
  userRole: UserRole | null
  adminLevel?: 'full_admin' | 'technician' | null
  userName?: string
  userEmail?: string
}

export const TopBar = ({ userRole, adminLevel, userName = 'Guidione Machava', userEmail }: TopBarProps) => {
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [showUserMenu, setShowUserMenu] = useState(false)

  const handleSignOut = async () => {
    await signOut()
  }

  const handleProfileSettings = () => {
    setShowUserMenu(false)
    navigate('/settings')
  }


  const getRoleBadge = (role: UserRole | null) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800'
      case 'manager':
        return 'bg-blue-100 text-blue-800'
      case 'employee':
        return 'bg-green-100 text-green-800'
      case 'billing':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getRoleLabel = (role: UserRole | null) => {
    switch (role) {
      case 'admin':
        return 'Admin'
      case 'manager':
        return 'Manager'
      case 'employee':
        return 'Employee'
      case 'billing':
        return 'Billing'
      default:
        return 'User'
    }
  }

  const getAdminLevelBadge = (level: 'full_admin' | 'technician' | null | undefined) => {
    switch (level) {
      case 'full_admin':
        return 'bg-yellow-100 text-yellow-800'
      case 'technician':
        return 'bg-blue-100 text-blue-800'
      default:
        return ''
    }
  }

  const getAdminLevelLabel = (level: 'full_admin' | 'technician' | null | undefined) => {
    switch (level) {
      case 'full_admin':
        return 'Full Admin'
      case 'technician':
        return 'Technician'
      default:
        return null
    }
  }

  return (
    <div className="px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Search Input */}
        <div className="flex-1 max-w-2xs">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            />
          </div>
        </div>

        {/* User Profile */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{userName}</p>
                <p className="text-xs text-gray-500">{userEmail}</p>
              </div>
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-sm">{getInitials(userName)}</span>
              </div>
            </div>
          </button>

          {/* User Dropdown Menu */}
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">{userName}</p>
                <p className="text-xs text-gray-500">{userEmail}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleBadge(userRole)}`}>
                    {getRoleLabel(userRole)}
                  </span>
                  {adminLevel && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getAdminLevelBadge(adminLevel)}`}>
                      {getAdminLevelLabel(adminLevel)}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="py-1">
                <button
                  onClick={handleProfileSettings}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <User className="w-4 h-4 mr-3" />
                  Profile Settings
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
